import "./SpaceHomeV2.scss";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";
import { THEME, ThemeProvider } from "App/common/ThemeProvider";
import { UtilityGutterWidthProvider } from "App/common/useUtilityGutterWidth";
import { useSpaceContext } from "App/common/useSpace";
import { useAuth } from "App/components/Auth";
import {
  checkIsUserAnonymous,
  computeSpaceMemberIds,
  getEntityShareKeys,
  getEntitySubscribers,
  isSpacePrivateInboxEnabled,
  PERMISSION,
  stripExtension,
} from "@highnote/server/src/core/shared-util";
import { useHistory } from "react-router";
import {
  useGlobalAudioPlayer,
  useHTMLAudioPlayer,
} from "App/components/GlobalAudioPlayer";
import {
  GlobalAudioPlayer,
  GlobalAudioTrackPreview,
} from "App/components/GlobalAudioPlayer/GlobalAudioPlayer";
import {
  AttachmentsContextProvider,
  useAttachmentsContext,
} from "App/common/useAttachments";
import { EntitySortMenu } from "App/components/EntityLibrary/EntitySortMenu";
import { FileDrop } from "App/common/FileDrop";
import { UPLOAD_GROUP, useFiles } from "App/components/useFiles";
import { highnote } from "@highnote/server/src/sdk";
import { SpaceHomeNavigation } from "./SpaceNavigation";
import { useToast } from "App/common/useToast";
import { useNotificationsContext } from "App/common/useNotifications";
import {
  AUDIO_ACCEPT_TYPES,
  COLLECTION_ID,
  JOB_STATUS,
  KNOCK_WORKFLOW,
  ROLE,
  Roles,
} from "@highnote/server/src/core/entities";
import { useEntityShareKeyInUrl } from "App/components/ShareDialog/Members/InviteByLink";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";
import { SpaceItems } from "./SpaceItems";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { DownloadRequestsContextProvider } from "App/components/useEntities/useDownloadRequests";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import { DropboxJobsContextProvider } from "App/components/useEntities/useDropboxDownloads";
import {
  DraftSpaceOnboardDialog,
  PrivateInboxVisitDialog,
} from "./SpaceDialogs";
import {
  APP_FEATURES,
  AppFeaturesStatus,
} from "@highnote/server/src/core/features";
import { useCreatedTracks } from "App/components/useEntities/useCreatedTracks";
import { JoinSpaceRequestDialog } from "./JoinSpaceRequestDialog";
import { ConnectedUserName } from "App/common/UserAvatar/UserAvatar";
import { useUrlContext } from "../useUrlContext";
import { useMoveToSpace } from "App/components/useEntities/useLibraryTracks/useMoveToSpace";
import { useMobileAppParams } from "../useMobileAppParams";
import { SpaceHero } from "./SpaceHero/SpaceHero";
import { SpaceCountdownPanel } from "./SpaceCountdownPanel";
import { useCountdown } from "App/common/useCountdown";
import { Container } from "App/common/Container/Container";
import { SpaceChatSidebar } from "App/components/Sidebar/SpaceChatSidebar";
import { SpacePageActions } from "./SpacePageActions/SpacePageActions";
import { Skeleton } from "App/common/Skeleton/Skeleton";
import { ReactComponent as SpacePlaylistSVG } from "App/common/icons-v2/space-template-playlist.svg";
import { ReactComponent as SpacePlaylistWithChatSVG } from "App/common/icons-v2/space-template-playlist-with-chat.svg";
import { ReactComponent as SpaceBlankSVG } from "App/common/icons-v2/space-template-blank.svg";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import { useAddToSpace } from "App/components/useEntities/useLibraryTracks/useAddToSpace";
import { EntitySelection } from "App/components/EntityLibrary/EntitySelection";
import { AdsContextProvider } from "App/common/Ads/AdsContext";
import { useGlobalSpaceTracks } from "App/store/spaceTracks/useGlobalSpaceTracks";

const SpaceHomeUI = () => {
  const { spaceId, trackId } = useUrlContext();
  const { showMoveToSpaceDialog, showAddToSpaceDialog } = useMobileAppParams();
  const { openMoveToSpace } = useMoveToSpace();
  const { openAddToSpace } = useAddToSpace();
  const { user, authLoading, joinEntity, isAllowed } = useAuth();
  const { toasted } = useToast();
  const { space, itemSortType, setItemSortType, spaceLoading } =
    useSpaceContext();
  const { globalSpaces } = useGlobalSpaces();
  const { globalTracks } = useGlobalTracks();
  const { createdTracks } = useCreatedTracks();
  const history = useHistory();
  const { getChildSpaces, getChildTracks } = useChildEntityWatchers();
  const [isTemplateSelectionLoading, setIsTemplateSelectionLoading] =
    useState(false);
  const [hasSelectedTemplate, setHasSelectedTemplate] = useState(false);
  const [areUploadsLoading, setAreUploadsLoading] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const requester = searchParams.get("requester");

  const subscribers = getEntitySubscribers(space);
  const isMember = subscribers.includes(user?.id);
  // Compute memberIds directly from `rolesV2` instead of reading the
  // `space.memberIds` field, which is populated onWrite and so can lag if a user just joined a space
  const memberIds = useMemo(
    () => computeSpaceMemberIds(space?.rolesV2, space?.createdBy),
    [space?.rolesV2, space?.createdBy],
  );
  const isSpaceMember = memberIds.includes(user?.id);
  const canManageSpace = isAllowed(PERMISSION.TO_MANAGE_SPACE, { space });
  const isInboxGuest = isSpacePrivateInboxEnabled(space) && !isMember;

  // Launch countdown, shown above the library only while it's still counting
  // down to a future `countdownFrom`.
  const launchCountdown = useCountdown(space?.countdownFrom);

  const { globalSpaceTracks } = useGlobalSpaceTracks();

  const childSpaces = useMemo(
    () => getChildSpaces(space?.id),
    [globalSpaces.childSpaces.get(space.id)],
  );

  const spaceTracks = useMemo(() => {
    return isInboxGuest ? createdTracks : getChildTracks(space?.id);
    // `getChildTracks` builds a tile per child id from BOTH the
    // space-tracks-v1 row and the canonical doc, so a header change on
    // either (a mirrored title, artwork, a doc arriving) must rebuild —
    // not only a change in membership.
  }, [
    isInboxGuest,
    globalTracks.childTracks.get(space.id),
    globalTracks.tracks,
    globalSpaceTracks.spaceTrackRows,
    createdTracks,
  ]);

  // `getChildTracks` resolves a borrowed track from its row even before the
  // canonical doc arrives, so the tile count IS the child count. (Only an
  // id backed by neither a row nor a doc is dropped, and membership is
  // populated from exactly those two sources.)
  const childTrackCount = spaceTracks.length;

  const spaceAttachments = useMemo(() => {
    return isInboxGuest ? [] : space?.files || [];
  }, [isInboxGuest, space?.files]);

  const { markAsRead, notifications } = useNotificationsContext();
  const { getUploads, uploadFiles, getUploadCache, removeUpload } = useFiles();
  const { attachFileToContext, attachments } = useAttachmentsContext();
  useEntityShareKeyInUrl(space);

  const [isPrivateInboxModalOpen, setIsPrivateInboxModalOpen] = useState(false);
  const [isDraftSpaceModalOpen, setIsDraftSpaceModalOpen] = useState(false);
  const [isJoinSpaceRequestDialogOpen, setIsJoinSpaceRequestDialogOpen] =
    useState(Boolean(requester));

  const isUserAnonymous = useMemo(
    () => checkIsUserAnonymous(user?.id),
    [user?.id],
  );

  useEffect(() => {
    const unlisten = history.listen(() => {
      const searchParams = new URLSearchParams(location.search);
      setIsJoinSpaceRequestDialogOpen(Boolean(searchParams.get("requester")));
    });
    return () => {
      unlisten();
    };
  }, []);

  const fileDragRef = useRef<HTMLDivElement>();
  const unmountedRef = useRef<boolean>(false);

  const canAdd = isAllowed(PERMISSION.TO_ADD_TO_SPACE, { space });

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    if (showMoveToSpaceDialog && spaceId && !trackId && space) {
      openMoveToSpace({
        entity: space,
        entityType: COLLECTION_ID.SPACE,
      });
    }
  }, [spaceId, trackId, showMoveToSpaceDialog, space]);

  useEffect(() => {
    if (showAddToSpaceDialog && !trackId && space) {
      openAddToSpace(space);
    }
  }, [trackId, showAddToSpaceDialog, space]);

  useEffect(() => {
    if (!space) return;
    const unreads = notifications.filter(
      (n) =>
        n.source.key === KNOCK_WORKFLOW.SPACE_MEMBER_ADDED &&
        n.data.spaceId === space?.id &&
        !n.read_at,
    );
    markAsRead(unreads);
  }, [space, notifications]);

  const onApproveJoinSpaceRequest = useCallback(
    async (userId: string) => {
      const newRoles: Roles = {
        [`rolesV2,${userId}`]: [
          ROLE.VIEW,
          ROLE.COMMENT,
          ROLE.REPLY,
          ROLE.REACT,
          ROLE.UPLOAD,
          ROLE.DOWNLOAD,
        ],
      };

      await toasted({
        promise: highnote.updateSpace({
          id: space.id,
          data: newRoles,
        }),
        successMessage: (
          <span>
            <ConnectedUserName userId={searchParams.get("requester")} />
            {"  "}
            {`was added to ${space.name}.`}
          </span>
        ),
        errorMessage: (
          <span>
            <ConnectedUserName userId={searchParams.get("requester")} />
            {"  "}
            {`could not be added to ${space.name}.`}
          </span>
        ),
      })
        .then(() => {
          setIsJoinSpaceRequestDialogOpen(false);
          // Clear the requester from URL immediately to prevent the dialog from re-rendering with null
          history.replace(`/space/${space.id}`);
        })
        .catch(() => {
          // Keep dialog open on error
        });
    },
    [toasted, space],
  );

  const onUploadFiles = useCallback(
    async (files: File[]) => {
      // if the user has already been introduced to the Private Inbox,
      if (isPrivateInboxModalOpen) {
        setIsPrivateInboxModalOpen(false);
      }

      if (files.length < 1) return;
      setAreUploadsLoading(true);
      const uploadPayloads = files.map((file) => {
        const asTrack = AUDIO_ACCEPT_TYPES.includes(file.type);
        const cache = asTrack
          ? getUploadCache(UPLOAD_GROUP.TRACKS_BY_SPACE, space?.id)
          : getUploadCache(UPLOAD_GROUP.ATTACHMENTS_BY_SPACE, space?.id);
        return {
          file,
          cache,
        };
      });

      await uploadFiles({
        payloads: uploadPayloads,
        onSuccess: async (file, fileEntity) => {
          const asTrack = AUDIO_ACCEPT_TYPES.includes(file.type);
          if (asTrack) {
            await highnote.createTrack({
              id: uuidv4(),
              data: {
                title: stripExtension(fileEntity.fileName),
                versionFilesV2: [fileEntity.id],
                spaceId: space?.id,
              },
            });
          } else {
            await attachFileToContext(fileEntity.id);
          }

          joinEntity({
            entity: space,
            entityType: "Space",
            trigger: JOIN_ENTITY_TRIGGER.UPLOAD,
          });

          removeUpload(fileEntity.id);
        },
      });

      setAreUploadsLoading(false);
    },
    [space?.id, user],
  );

  const spaceItems = [...spaceTracks, ...childSpaces, ...spaceAttachments];

  const attachmentsAreDownloadable = attachments.some((attachment) => {
    return Boolean(attachment.fileType);
  });

  useEffect(() => {
    if (!user || !space) return;

    const draftSpaceOnboarded = Boolean(user?.draftSpaceOnboarded);
    if (
      !draftSpaceOnboarded &&
      space.isDraftSpace &&
      space.createdBy === user.id
    ) {
      setIsDraftSpaceModalOpen(true);
    }

    const hasIntroducedToPrivateInbox = Boolean(
      user?.hasIntroducedToPrivateInbox,
    );
    const isPrivateInboxEnabled = isSpacePrivateInboxEnabled(space);
    if (!hasIntroducedToPrivateInbox && isPrivateInboxEnabled) {
      setIsPrivateInboxModalOpen(true);
    }
  }, [user, space]);

  const isImplicitlyNewSpace =
    childTrackCount === 0 &&
    childSpaces.length === 0 &&
    spaceAttachments.length === 0 &&
    !space.description &&
    !space.artworkFile &&
    !space.chatEnabled;

  const uploads = [
    ...getUploads({
      cache: getUploadCache(UPLOAD_GROUP.TRACKS_BY_SPACE, space?.id),
    }),
    ...getUploads({
      cache: getUploadCache(UPLOAD_GROUP.ATTACHMENTS_BY_SPACE, space?.id),
    }),
  ];

  const hasUploads = uploads.length > 0;
  const isTemplateSelected = typeof space.spaceSetup === "boolean";

  // If the user has the permission to manage the space, they can see the getting started options
  // space.spaceSetup is a boolean when a template is selected. Undefined when a template is not selected.
  // space.spaceSetup = true when a template is a blank space, and false when a template is a playlist or playlistWithChat.
  // We use space.spaceSetup to identify if a template was selected and if that template is a blank space.
  // TODO: Find a better way to identify if a template was selected and if that template is a blank space.
  // TODO: Find a better type for space.spaceSetup
  const showGettingStartedOptions =
    isAllowed(PERMISSION.TO_MANAGE_SPACE, { space }) &&
    isImplicitlyNewSpace &&
    !isTemplateSelected &&
    !areUploadsLoading &&
    !hasUploads &&
    !hasSelectedTemplate;

  const playListDescription = "Share demos and EPK's";
  const playlistWithChatDescription =
    "Share demos, EPK's and chat with your team";

  // Each template initializes with different default configurations:
  // - Playlist: artwork=true, description=true, chat=false, spaceSetup=false
  // - Workspace: artwork=true, description=true, chat=true, spaceSetup=false
  // - Blank: artwork=false, description=false, chat=false, spaceSetup=true
  // For more info see @highnote/client/README.md#new-space-templates
  const onTemplateClick = async (
    template: "playlist" | "playlistWithChat" | "blank",
  ) => {
    setIsTemplateSelectionLoading(true);
    setHasSelectedTemplate(true);
    await toasted({
      promise: highnote.updateSpace({
        id: space.id,
        data: {
          // If the template is a blank space, set spaceSetup to true
          // If the template is a playlist or playlistWithChat, set spaceSetup to false
          // spaceSetup is undefined when a template is not selected.
          // TODO: Find a better way to identify if a template was selected and if that template is a blank space.
          // TODO: Find a better type for space.spaceSetup
          spaceSetup: template === "blank",
          description:
            template === "playlist"
              ? playListDescription
              : template === "playlistWithChat"
                ? playlistWithChatDescription
                : "",
          chatEnabled: template === "playlistWithChat",
        },
      }),
      errorMessage: `Could not apply the ${template} template to ${space.name}. Please try again.`,
    }).finally(() => {
      setIsTemplateSelectionLoading(false);
    });
  };

  // Whether the space's share link grants VIEW — drives the panel's open status
  // (LIVE vs PRIVATE), matching SpaceNavigation's share indicator.
  const isLinkShared = (() => {
    const [shareKey] = getEntityShareKeys(space);
    return (space?.rolesV2?.[shareKey] || []).includes(ROLE.VIEW);
  })();

  // Rendered in two places and toggled by breakpoint in SpaceHomeV2.scss: it
  // scrolls with the content on desktop, and is pinned below the scroll area on
  // mobile. The panel picks the member CTA (You're In vs Download the App) by
  // breakpoint itself, so both call sites render the same thing.
  const renderCountdownPanel = () => (
    <SpaceCountdownPanel
      countdown={launchCountdown}
      members={memberIds.map((id) => ({ id }))}
      memberCount={memberIds.length}
      joined={authLoading ? undefined : isSpaceMember}
      canManage={canManageSpace}
      isLinkShared={isLinkShared}
      onJoin={() =>
        joinEntity({
          entity: space,
          entityType: "Space",
          trigger: JOIN_ENTITY_TRIGGER.BUTTON,
        })
      }
    />
  );

  // Desktop: non-members always see the panel (it carries the join CTA);
  // members only see it while a countdown is actually running. The pinned mobile
  // copy is always rendered, countdown or not, since it is the primary CTA
  // surface there.
  const showInlineCountdown = !isSpaceMember || launchCountdown.isActive;

  return (
    <ThemeProvider theme={THEME.DARK}>
      <UtilityGutterWidthProvider
        className="highnote-space-home"
        data-cypress-id="highnote-space-home"
      >
        {canAdd && (
          <FileDrop
            dragRef={fileDragRef}
            onDrop={onUploadFiles}
            instruction={"Add files to this Space by dropping them here"}
          />
        )}

        {AppFeaturesStatus[APP_FEATURES.PRIVATE_INBOX] && (
          <PrivateInboxVisitDialog
            spaceOwner={space.createdBy}
            isOpen={isPrivateInboxModalOpen}
            onUploadFiles={onUploadFiles}
            onClose={() => {
              setIsPrivateInboxModalOpen(false);
              highnote.updateUser({
                id: user.id,
                data: { hasIntroducedToPrivateInbox: true },
              });
            }}
          />
        )}
        <DraftSpaceOnboardDialog
          isOpen={isDraftSpaceModalOpen}
          onClose={() => {
            setIsDraftSpaceModalOpen(false);

            highnote.updateUser({
              id: user.id,
              data: { draftSpaceOnboarded: true },
            });
          }}
        />

        {isJoinSpaceRequestDialogOpen && (
          <JoinSpaceRequestDialog
            onClose={() => {
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("requester");
              history.replace(
                location.pathname + "?" + searchParams.toString(),
              );
              setIsJoinSpaceRequestDialogOpen(false);
            }}
            requesterId={requester}
            spaceName={space.name}
            onApprove={onApproveJoinSpaceRequest}
            onDeny={() => {
              setIsJoinSpaceRequestDialogOpen(false);
              history.replace(`/space/${space.id}`);
            }}
          />
        )}

        <SpaceHomeNavigation />

        <div
          className={classNames("content-container", {
            "chat-open": space.chatEnabled,
          })}
          ref={fileDragRef}
        >
          <div
            // Created an outer div to allow the floating action button to
            // seem as a sticky position. Children of a scrollable container
            // conflict with position: sticky, so this is a workaround.
            className={classNames("main-content-container", {
              "full-width": !space.chatEnabled,
            })}
          >
            <div className="scrollable">
              <Container
                className="space-hero-container"
                variant="container-xl"
              >
                <SpaceHero
                  spaceItemsCount={spaceItems.length}
                  spaceTracksCount={spaceTracks.length}
                  hasDownloadableItems={
                    attachmentsAreDownloadable ||
                    spaceTracks.length > 0 ||
                    childSpaces.length > 0
                  }
                />

                {showInlineCountdown && (
                  <div className="space-launch-countdown space-launch-countdown--inline">
                    {renderCountdownPanel()}
                  </div>
                )}
              </Container>

              <div
                className="entity-library-container"
                data-cypress-id="library-container"
              >
                {spaceLoading ? (
                  <div className="entity-library-items-sort-row">
                    <Skeleton width={32} height={24} />
                    <Skeleton width={32} height={24} />
                  </div>
                ) : (
                  <>
                    {spaceItems.length > 0 &&
                      (isMember || !isUserAnonymous) && (
                        <div className="count-sort-container">
                          {isMember && (
                            <EntitySortMenu
                              sortType={itemSortType}
                              onChange={setItemSortType}
                            />
                          )}
                          {!isUserAnonymous && <EntitySelection />}
                        </div>
                      )}
                  </>
                )}

                {isTemplateSelectionLoading ? (
                  <div className="space-template-options">
                    <LoadingScreen />
                  </div>
                ) : showGettingStartedOptions ? (
                  <div className="space-template-options">
                    <span className="space-template-options-title">
                      Get started with a template:
                    </span>
                    <Button
                      theme={BUTTON_THEME.PRIMARY}
                      size={BUTTON_SIZE.AUTO}
                      className="space-template-option"
                      onClick={() => onTemplateClick("playlistWithChat")}
                      data-cypress-id="playlistWithChat-template-option"
                    >
                      <SpacePlaylistWithChatSVG />
                      <div className="space-template-option-text">
                        <span>Playlist with Chat</span>
                        <span>{playlistWithChatDescription}</span>
                      </div>
                    </Button>
                    <Button
                      theme={BUTTON_THEME.PRIMARY}
                      size={BUTTON_SIZE.AUTO}
                      className="space-template-option"
                      onClick={() => onTemplateClick("playlist")}
                      data-cypress-id="playlist-template-option"
                    >
                      <SpacePlaylistSVG />
                      <div className="space-template-option-text">
                        <span>Playlist</span>
                        <span>{playListDescription}</span>
                      </div>
                    </Button>
                    <span className="space-template-options-title">OR</span>
                    <Button
                      theme={BUTTON_THEME.PRIMARY}
                      size={BUTTON_SIZE.AUTO}
                      className="space-template-option"
                      onClick={() => onTemplateClick("blank")}
                      data-cypress-id="blank-template-option"
                    >
                      <SpaceBlankSVG />
                      <div className="space-template-option-text">
                        <span>Blank space</span>
                        <span>Customize your space from scratch</span>
                      </div>
                    </Button>
                  </div>
                ) : (
                  <SpaceItems sortType={itemSortType} />
                )}
              </div>
            </div>

            <div className="space-launch-countdown space-launch-countdown--pinned">
              {renderCountdownPanel()}
            </div>

            {canAdd && (
              <SpacePageActions
                onUploadFiles={onUploadFiles}
                triggerProps={{
                  className: "floating-action-button-container",
                }}
              />
            )}
          </div>
          {/* SpaceChatSidebar renders null when chat is off, but this wrapper
              is a fixed 365px flex rail: left unconditionally mounted it
              renders as an empty column that still squeezes the main content,
              so gate it on the same flag the sidebar uses. */}
          {space.chatEnabled && (
            <div className="entity-space-chat-container">
              <div className="entity-space-chat">
                <SpaceChatSidebar isOutsideSidebar />
              </div>
            </div>
          )}
        </div>
      </UtilityGutterWidthProvider>
    </ThemeProvider>
  );
};

const SpaceHomePlayer = () => {
  const history = useHistory();
  const { spaceId } = useSpaceContext();
  const { goToTrack, nowPlaying, prevTrackId } = useGlobalAudioPlayer();
  const { useHTMLPlayer, currentTime, onSeek } = useHTMLAudioPlayer();

  const playbackProps = {
    ...(useHTMLPlayer && {
      onPrev: () => {
        if (currentTime > 1) {
          onSeek(0);
          return;
        }
        goToTrack(prevTrackId);
      },
    }),
  };

  const preview = useMemo(() => {
    const track = nowPlaying.track;

    return (
      <GlobalAudioTrackPreview
        track={track}
        onClick={() => {
          history.push(`/space/${spaceId}/${track.id}`);
        }}
      />
    );
  }, [nowPlaying.track, spaceId]);

  return <GlobalAudioPlayer preview={preview} {...playbackProps} />;
};

export const SpaceHome = () => {
  const { space, spaceId } = useSpaceContext();
  return (
    <AttachmentsContextProvider
      space={space}
      spaceId={spaceId}
      attachmentIds={space?.files}
    >
      <DownloadRequestsContextProvider isProcessing>
        <DropboxJobsContextProvider
          spaceId={space?.id}
          statuses={[
            JOB_STATUS.PENDING,
            JOB_STATUS.IN_PROGRESS,
            JOB_STATUS.FAILED,
          ]}
        >
          {/*
            Mount the ads provider at the SpaceHome boundary so every
            child (notably `SpaceItems` via `SpaceHomeUI`) has access
            to `pinnedAds`. SpaceTrack mounts its own provider at a
            lower level for the carousel — these two providers live on
            sibling routes and never stack.
          */}
          <AdsContextProvider spaceId={space?.id}>
            <SpaceHomeUI />
          </AdsContextProvider>
        </DropboxJobsContextProvider>
      </DownloadRequestsContextProvider>
      <SpaceHomePlayer />
    </AttachmentsContextProvider>
  );
};
